"use client";

import { ArrowRight } from "lucide-react";
import { Link } from "@/components/common/Link";
import { Button } from "@/components/ui/button";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
export default function RestorativeSleepSolutions() {
  const {
    ref: leftRef,
    isVisible: leftVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  const {
    ref: rightRef,
    isVisible: rightVisible
  } = useRevealOnScroll<HTMLDivElement>({
    threshold: 0.15
  });
  return <section data-section-id="3198" id="restorative-sleep-solutions" className="bg-background text-foreground relative overflow-hidden py-20 md:py-24 lg:min-h-[90vh] lg:py-0 lg:flex lg:items-center">
      {}
      <div aria-hidden="true" className="pointer-events-none absolute inset-0 opacity-[0.03]" style={{
      backgroundImage: "repeating-linear-gradient(to right, var(--foreground) 0, var(--foreground) 1px, transparent 1px, transparent 96px)"
    }} />

      <div className="relative mx-auto w-full max-w-7xl px-6 md:px-10 lg:px-12">
        <div className="grid grid-cols-1 gap-12 lg:grid-cols-12 lg:gap-16 lg:items-center">
          {}
          <div ref={leftRef} className={`order-2 lg:order-1 lg:col-span-6 transition-all duration-1000 ease-out ${leftVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-6"}`}>
            {}
            <div className="mb-8 flex items-center gap-3">
              <span className="h-px w-8 bg-chart-2/70" />
              <p className="font-mono text-[0.75rem] font-bold uppercase tracking-[0.15em] text-chart-2" style={{
              letterSpacing: "0.15em"
            }}>
                Sleep Medicine · Dr. Azmat Qayyum
              </p>
            </div>

            {}
            <h1 className="font-default font-semibold text-foreground leading-[1.1] tracking-tighter" style={{
            fontSize: "clamp(2.5rem, 5vw, 3.5rem)"
          }}>
              Sleep Medicine & <span className="italic font-serif font-medium text-chart-2">Sleep Apnea</span> Care
            </h1>

            {}
            <div className="mt-10 max-w-xl space-y-5">
              <p className="font-default text-base md:text-lg leading-relaxed text-slate-text">
                Sleep problems can affect your breathing, energy, and overall health. Chaudhry Pulmonary Associates provides adult sleep medicine care for sleep apnea, CPAP follow-up, oxygen-related concerns, and sleep-related breathing issues. Our team works with patients to review symptoms, testing, and treatment options clearly.
              </p>
            </div>

            {}
            <div className="mt-12 flex flex-col sm:flex-row sm:items-center gap-6 sm:gap-8">
              <Link to="tel:+14126729240">
                <Button size="lg" className="bg-primary text-primary-foreground hover:bg-chart-2 h-12 px-8 rounded-md font-default text-sm font-semibold tracking-wide transition-all">
                  Call (412) 672-9240
                </Button>
              </Link>
              <Link onClick={e => {
              e.preventDefault();
              document.getElementById("sleep-services")?.scrollIntoView({
                behavior: "smooth"
              });
            }} className="group inline-flex items-center gap-2 font-default text-sm font-semibold tracking-wide text-foreground hover:text-chart-2 transition-colors cursor-pointer">
                <span className="border-b border-foreground/30 group-hover:border-chart-2 pb-0.5">
                  Explore Sleep Services
                </span>
                <ArrowRight className="h-4 w-4 transition-transform group-hover:translate-x-1" />
              </Link>
            </div>
          </div>

          {}
          <div ref={rightRef} className={`order-1 lg:order-2 lg:col-span-6 transition-all duration-1000 ease-out delay-150 ${rightVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8"}`}>
            <div className="relative mx-auto w-full max-w-md lg:max-w-none">
              {}
              <div aria-hidden="true" className="absolute -inset-6 rounded-md bg-secondary/50 blur-3xl" />

              {}
              <div className="relative overflow-hidden rounded-md shadow-xl border border-border">
                <img src="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/sleep_medicine_consultation.png" data-wvc-srcset="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/sleep_medicine_consultation.png-wvc-srcset" data-wvc-sizes="https://wpvc-images.s3.us-east-1.amazonaws.com/images/1692402/img/sleep_medicine_consultation.png-wvc-sizes" alt="A professional medical consultation environment focusing on sleep health and patient care" loading="lazy" decoding="async" className="block w-full h-[520px] md:h-[640px] lg:h-[720px] object-cover" />

                {}
                <div aria-hidden="true" className="pointer-events-none absolute inset-0 rounded-md" style={{
                boxShadow: "inset 0 1px 0 0 hsla(0, 0%, 100%, 0.1)"
              }} />
              </div>

              {}
              <div className="absolute bottom-6 left-6 md:bottom-8 md:left-8">
                <div className="rounded-md border border-border bg-white/90 px-5 py-4 shadow-lg backdrop-blur-md">
                  <p className="font-mono text-[0.75rem] font-bold uppercase tracking-[0.15em] text-chart-2 mb-1.5">
                    Clinical Excellence
                  </p>
                  <p className="font-default text-sm md:text-base text-primary font-semibold tracking-wide">
                    Adult Pulmonary & Sleep Medicine
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>;
}